import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { AlertTriangle } from 'lucide-react';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ✅ Selectores específicos — evita re-renders por cambios en token/user
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const refreshAccessToken = useAuthStore((s) => s.refreshAccessToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const sessionId = useAuthStore((s) => s.sessionId);

  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activityThrottleRef = useRef<number>(0);
  const mountedRef = useRef(true);

  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);

  // =============================================
  // ⏱️ TIEMPOS CONFIGURABLES
  // =============================================
  const INACTIVITY_TIMEOUT = 25 * 60 * 1000;     // 25 min → logout
  const WARNING_BEFORE = 2 * 60 * 1000;           // Mostrar modal 2 min antes
  const TOKEN_REFRESH_INTERVAL = 13 * 60 * 1000;  // 13 min (token expira en 15)
  const ACTIVITY_THROTTLE = 60 * 1000;             // Máximo 1 heartbeat/min al backend

  // =============================================
  // 🧹 CLEANUP
  // =============================================
  const clearAllTimers = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  // =============================================
  // 🚪 LOGOUT FORZADO
  // =============================================
  const forceLogout = useCallback(async (reason: string = 'inactivity') => {
    console.warn(`⏰ [AuthProvider] Logout por: ${reason}`);
    clearAllTimers();
    setShowWarning(false);

    try {
      await clearAuth();
    } catch (e) {
      console.error('[AuthProvider] Error en clearAuth:', e);
    }

    // ✅ Forzar limpieza manual por si clearAuth no completó
    localStorage.removeItem('token');
    localStorage.removeItem('kikai_token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('user');
    localStorage.removeItem('kikai-auth-storage');

    window.location.href = `/login?reason=${reason}`;
  }, [clearAllTimers, clearAuth]);

  // =============================================
  // ⏰ RESET TIMER DE INACTIVIDAD
  // =============================================
  const resetInactivityTimer = useCallback(() => {
    // Limpiar timers anteriores
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    
    if (!mountedRef.current) return;
    setShowWarning(false);

    // Timer para mostrar warning (23 min)
    warningTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      console.log('⚠️ [AuthProvider] Mostrando warning de inactividad');
      setShowWarning(true);
      setTimeLeft(Math.floor(WARNING_BEFORE / 1000));
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);

    // Timer para logout forzado (25 min)
    inactivityTimerRef.current = setTimeout(() => {
      forceLogout('inactivity');
    }, INACTIVITY_TIMEOUT);
  }, [INACTIVITY_TIMEOUT, WARNING_BEFORE, forceLogout]);

  // =============================================
  // 🔄 TOKEN REFRESH + INACTIVIDAD + ACTIVITY
  // =============================================
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    mountedRef.current = true;

    // 1️⃣ Iniciar timer de inactividad
    resetInactivityTimer();

    // 2️⃣ Refresh automático de token cada 13 min
    refreshIntervalRef.current = setInterval(async () => {
      try {
        await refreshAccessToken();
        console.log('🔄 [AuthProvider] Token renovado automáticamente');
      } catch (error: any) {
        console.error('[AuthProvider] Token refresh falló:', error?.message);
        // Si el refresh falla por sesión inválida, logout
        const code = error?.response?.data?.code;
        if (code === 'INVALID_SESSION' || code === 'SESSION_TIMEOUT') {
          forceLogout(code.toLowerCase());
        }
      }
    }, TOKEN_REFRESH_INTERVAL);

    // 3️⃣ Activity tracking con THROTTLE
    const handleActivity = () => {
      const now = Date.now();
      // Solo procesar si pasó más de 1 minuto desde última actividad
      if (now - activityThrottleRef.current < ACTIVITY_THROTTLE) return;
      activityThrottleRef.current = now;

      // Resetear timer local
      resetInactivityTimer();

      // Heartbeat al backend (sin bloquear UI)
      const currentSessionId = useAuthStore.getState().sessionId;
      if (currentSessionId) {
        fetch('/api/auth/refresh-activity', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: currentSessionId }),
        }).catch(() => {
          // Silenciar - no es crítico
        });
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => document.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      mountedRef.current = false;
      clearAllTimers();
      events.forEach((e) => document.removeEventListener(e, handleActivity));
    };
  }, [isAuthenticated]);

  // =============================================
  // ⏳ COUNTDOWN del warning
  // =============================================
  useEffect(() => {
    if (!showWarning) return;

    const countdown = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          forceLogout('inactivity');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showWarning, forceLogout]);

  // =============================================
  // ✅ CONTINUAR SESIÓN
  // =============================================
  const handleContinue = useCallback(() => {
    setShowWarning(false);
    setTimeLeft(120);
    activityThrottleRef.current = 0; // Reset throttle para heartbeat inmediato
    resetInactivityTimer();

    // Heartbeat inmediato al continuar
    const currentSessionId = useAuthStore.getState().sessionId;
    if (currentSessionId) {
      fetch('/api/auth/refresh-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: currentSessionId }),
      }).catch(() => {});
    }
  }, [resetInactivityTimer]);

  // =============================================
  // 🖼️ RENDER
  // =============================================
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <>
      {children}

      {/* ✅ MODAL INTEGRADO — reemplaza InactivityWarningModal externo */}
      {showWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 max-w-md w-full mx-4 animate-in fade-in zoom-in duration-200">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Sesión por expirar
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Tu sesión expirará por inactividad en{' '}
                  <span className="font-bold text-yellow-600 dark:text-yellow-400">
                    {minutes}:{seconds.toString().padStart(2, '0')}
                  </span>
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  ¿Deseas continuar trabajando?
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleContinue}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Continuar sesión
                  </button>
                  <button
                    onClick={() => forceLogout('manual')}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Cerrar sesión
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};